// --- React · 라우팅 · 다국어 ---
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// --- 스타일 ---
import { css } from '@emotion/react';
import { breakpoints } from '@/theme/tokens/breakpoints';

// --- 전역 UI 스토어 (로딩, 알림, 선택) ---
import { useNotificationStore } from '../../../store/notificationStore';
import { useModalStore } from '@/store/modalStore';
import { useAppStore } from '../../../store/loadingStore';
import { useAuthStore } from '../../../store/authStore';
import { useSelectionStore } from '@/store/selectionStore';

// --- 공통 UI 컴포넌트 ---
import { useTheme } from '@/theme';
import {
  Button,
  Card,
  Flex,
  Spinner,
  Text,
  SummaryCard,
  CardTitle,
  Grid,
  IconButton,
  TextButton,
  InfoRow,
  Tag,
} from '@/components/ui';
import { DefaultContents } from '@/components/ui/common/DefaultContents';
import { formatPhoneForDisplay } from '@/components/ui/common/PhoneInput';

// --- 회사/사이트 스코프 훅 ---
import { useCompanySiteScope } from '@/hooks/useCompanySiteScope';

// --- API 훅: 회사 · 사이트 · 기기 ---
import { useGetCompanyManagerById } from '@/api/company/useCompany';
import { useGetCompanyManager } from '@/api/admin/useCompanyManagement';
import { useDeleteSite } from '@/api/site/useSite';
import {
  useGetSiteManagers,
  useCreateSiteManager,
  useDeleteSiteManager,
  type SiteManager,
} from '@/api/company/useSiteManagers';
import { useGetSiteDevices } from '@/api/device/useDevice';

// --- 타입 정의 ---
import type { SiteResponse } from '@packages/types/site';
import type { Device } from '@packages/types/device/dto';

// --- i18n · 에러 파싱 ---
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { parseCompanyApiError } from '@/utils/errors';
import { formatSubscriptionExpiryLine } from '@/utils/date';
import {
  isAdminUser,
  isCompanyManagerUser,
  isSiteManagerUser,
} from '@packages/types/auth/roles';

// --- 페이지 전용 모달 · 컴포넌트 ---
import { SiteCrudModal } from './SiteCrudModal';
import SiteManagerCrudModal from './SiteManagerCrudModal';
import CompanyInfoEditModal from './CompanyInfoEditModal';
import CompanyManagerEditModal from './CompanyManagerEditModal';
import DeviceReassignModal from './DeviceReassignModal';
import {
  RegistrationFormModal,
  type RegistrationFormData,
} from '@/components/modals/RegistrationFormModal';

const CompanyPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { showSuccess, showError, showWarning } = useNotificationStore();
  const { showConfirm, showTypedConfirm } = useModalStore();
  const { showLoading, hideLoading } = useAppStore();
  const { isAuthenticated, user } = useAuthStore();

  const {
    companies,
    currentCompany: company,
    sites,
    isAdmin,
    isCompanyManager,
    isSiteManager,
    isLoading: loading,
    loadingSites: isLoadingSites,
  } = useCompanySiteScope();

  const canManageSiteManagers = isCompanyManager || isAdmin;

  // ==================== 스타일 정의 ====================
  const styles = {
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: theme.fontSize.md,
    },
    tableHeader: {
      backgroundColor: theme.colors.surface.header.default,
    },
    tableHeaderCell: {
      padding: theme.spacing[6],
      textAlign: 'center' as const,
      fontWeight: theme.fontWeight.semibold,
      color: theme.colors.text.primary,
    },
    tableRow: {
      borderBottom: `1px solid ${theme.colors.border.container}`,
      textAlign: 'center' as const,
    },
    tableCell: {
      padding: theme.spacing[4],
    },
    expandedRow: {
      padding: theme.spacing[6],
      backgroundColor: theme.colors.surface.layout.background,
    },
    deviceTable: {
      width: '100%',
      fontSize: theme.fontSize.sm,
      borderCollapse: 'collapse' as const,
    },
    deviceTableHeader: {
      backgroundColor: theme.colors.surface.header.nested,
      textAlign: 'center' as const,
      fontWeight: theme.fontWeight.semibold,
      color: theme.colors.text.primary,
    },
  };

  // ==================== 상태 관리 ====================
  // 전역 상태 사용 (TopBar와 동기화)
  const { selectedCompanyId, setSelectedCompanyId } = useSelectionStore();
  const [showCompanyInfoModal, setShowCompanyInfoModal] = useState(false);
  const [showCompanyManagerModal, setShowCompanyManagerModal] = useState(false);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [editingSite, setEditingSite] = useState<SiteResponse | null>(null);
  const [showSiteManagerModal, setShowSiteManagerModal] = useState(false);
  const [showAddSiteManagerModal, setShowAddSiteManagerModal] = useState(false);
  const [editingSiteManager, setEditingSiteManager] = useState<SiteManager | null>(null);
  const [expandedSiteId, setExpandedSiteId] = useState<number | null>(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassigningDevice, setReassigningDevice] = useState<Device | null>(null);
  // 가이드 진행 중 여부 (기기가 없을 때 재할당 step 타겟을 위한 샘플 행 노출에 사용)
  const [isGuideActive, setIsGuideActive] = useState(false);

  // ==================== API 훅 ====================

  // Site Manager 목록 조회
  const {
    data: siteManagers = [],
    isLoading: loadingSiteManagers,
    refetch: refetchSiteManagers,
  } = useGetSiteManagers(company?.id || 0, {
    enabled: !!company?.id && canManageSiteManagers,
  });

  // Company Manager 정보 조회
  // Admin인 경우: 선택한 company의 manager 정보
  const { data: companyManagerById } = useGetCompanyManagerById(company?.id || 0, {
    enabled: !!company?.id && isAdmin,
  });
  // CompanyManager/SiteManager인 경우: 자신의 company manager 정보
  const { data: companyManager } = useGetCompanyManager({
    enabled: !!company?.id && !isAdmin && (isCompanyManager || isSiteManager),
  });
  // 통합 company manager 정보
  const companyManagerInfo = isAdmin ? companyManagerById : companyManager;

  // Mutation
  const deleteSiteMutation = useDeleteSite(company?.id || 0);
  const createSiteManagerMutation = useCreateSiteManager(company?.id || 0);
  const deleteSiteManagerMutation = useDeleteSiteManager(company?.id || 0);

  // 확장된 사이트의 기기 목록 조회
  const { data: siteDevices = [], isLoading: loadingSiteDevices } = useGetSiteDevices(
    expandedSiteId,
    {
      enabled: expandedSiteId !== null && (isAdmin || isCompanyManager),
    }
  );

  // ==================== 이펙트 ====================

  // 권한 확인: Admin/CompanyManager/SiteManager가 아닌 경우 접근 차단
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const allowed =
      isAdminUser(user) || isCompanyManagerUser(user) || isSiteManagerUser(user);
    if (!allowed) {
      showError(i18nKeys.company.need_site_manager_role, i18nKeys.company.need_site_manager_role);
      navigate('/');
    }
  }, [isAuthenticated, user, navigate, showError]);

  // 첫 번째 회사 자동 선택 (Admin/CompanyManager: 선택된 회사가 없을 때)
  useEffect(() => {
    if (!isSiteManager && companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [isSiteManager, companies, selectedCompanyId]);

  // Non-Admin(CompanyManager/SiteManager): 항상 자신의 회사로 고정
  useEffect(() => {
    if (!isAdmin && company) {
      setSelectedCompanyId(company.id);
    }
  }, [isAdmin, company]);

  // ==================== 핸들러 ====================

  const isSiteManagerOnly = isSiteManager && !isCompanyManager;

  const handleEditCompany = () => {
    if (isSiteManagerOnly) {
      showWarning('company:no_permission_edit_company');
      return;
    }
    setShowCompanyInfoModal(true);
  };

  const handleCreateSite = () => {
    if (isSiteManagerOnly) {
      showWarning('company:no_permission_create_site');
      return;
    }
    setEditingSite(null);
    setShowSiteModal(true);
  };

  const handleEditSite = (site: SiteResponse) => {
    if (isSiteManagerOnly) {
      showWarning('company:no_permission_edit_site');
      return;
    }
    setEditingSite(site);
    setShowSiteModal(true);
  };

  const handleDeleteSite = (site: SiteResponse) => {
    if (isSiteManagerOnly) {
      showWarning('company:no_permission_delete_site');
      return;
    }

    // 마지막 사이트 체크
    if (sites.length <= 1) {
      showWarning('company:site_delete_last_warning');
      return;
    }

    showTypedConfirm({
      title: t(i18nKeys.company.site_delete),
      message: t(i18nKeys.company.site_delete_confirm),
      confirmKeyword: site.name,
      confirmPrompt: t(i18nKeys.company.delete_type_name_prompt),
      variant: 'danger',
      onConfirm: () => {
        void (async () => {
          try {
            showLoading();
            await deleteSiteMutation.mutateAsync(site.id);
            showSuccess('company:site_delete_success');
          } catch (error: unknown) {
            const e = parseCompanyApiError(error);
            showError(e.title, e.message);
          } finally {
            hideLoading();
          }
        })();
      },
    });
  };

  const handleCloseSiteModal = () => {
    setShowSiteModal(false);
    setEditingSite(null);
  };

  const handleCreateSiteManager = () => {
    setShowAddSiteManagerModal(true);
  };

  const handleAddSiteManager = async (data: RegistrationFormData) => {
    // 전화번호는 선택 항목 — 모달 UI와 백엔드 DTO 모두 optional로 취급한다.
    if (!data.email) {
      showError(i18nKeys.company.site_manager_email_required);
      return;
    }
    if (!data.user_name) {
      showError(i18nKeys.auth.name_required);
      return;
    }

    try {
      showLoading();
      await createSiteManagerMutation.mutateAsync({
        email: data.email,
        name: data.user_name,
        phone: data.phone,
        notes: data.notes,
        language: (i18n.language || 'ko').split('-')[0],
      });
      // Site Manager 목록 즉시 새로고침
      await refetchSiteManagers();
      showSuccess('company:site_manager_create_success');
      setShowAddSiteManagerModal(false);
    } catch (error: unknown) {
      const e = parseCompanyApiError(error);
      showError(e.title, e.message);
    } finally {
      hideLoading();
    }
  };

  const handleEditSiteManager = (siteManager: SiteManager) => {
    // 가이드용 샘플 관리자는 실제 모달을 열지 않는다.
    if (siteManager.id < 0) return;
    setEditingSiteManager(siteManager);
    setShowSiteManagerModal(true);
  };

  const handleDeleteSiteManager = (siteManager: SiteManager) => {
    // 가이드용 샘플 관리자는 삭제 확인을 띄우지 않는다.
    if (siteManager.id < 0) return;
    showConfirm(
      t(i18nKeys.company.site_manager_delete),
      t(i18nKeys.company.site_manager_delete_confirm),
      () => {
        void (async () => {
          try {
            showLoading();
            await deleteSiteManagerMutation.mutateAsync(siteManager.id);
            // Site Manager 목록 즉시 새로고침
            await refetchSiteManagers();
            showSuccess('company:site_manager_delete_success');
          } catch (error: unknown) {
            const e = parseCompanyApiError(error);
            showError(e.title, e.message);
          } finally {
            hideLoading();
          }
        })();
      },
      undefined,
      'danger'
    );
  };

  const handleCloseSiteManagerModal = () => {
    setShowSiteManagerModal(false);
    setEditingSiteManager(null);
  };

  const handleToggleSiteDevices = (siteId: number) => {
    if (expandedSiteId === siteId) {
      setExpandedSiteId(null);
    } else {
      setExpandedSiteId(siteId);
    }
  };

  // 항상 최신 펼침 상태를 참조하기 위한 ref (이벤트 핸들러 클로저용)
  const expandedSiteIdRef = useRef<number | null>(null);
  useEffect(() => {
    expandedSiteIdRef.current = expandedSiteId;
  }, [expandedSiteId]);

  // 가이드 시작 시: 디바이스 재할당 등 펼쳐야 보이는 step 의 타겟을 미리 노출하기 위해
  // 기기가 있는 첫 사이트의 디바이스 목록을 자동으로 펼치고, 가이드가 끝나면 원래 상태로 되돌린다.
  useEffect(() => {
    const prevExpanded = { current: null as number | null };

    const handleGuideStart = (event: Event) => {
      const detail = (event as CustomEvent).detail as { guideKey?: string } | undefined;
      if (detail?.guideKey !== 'companyDetail') return;
      prevExpanded.current = expandedSiteIdRef.current;
      setIsGuideActive(true);
      // 기기가 있는 사이트를 우선 펼치고, 없으면 첫 사이트를 펼친다.
      // (기기가 없는 경우 렌더 단계에서 샘플 기기 행으로 재할당 step 타겟을 노출)
      const firstWithDevices = sites.find(site => (site.device_count || 0) > 0);
      const target = firstWithDevices ?? sites[0];
      if (target) {
        setExpandedSiteId(target.id);
      }
    };

    const handleGuideEnd = (event: Event) => {
      const detail = (event as CustomEvent).detail as { guideKey?: string } | undefined;
      if (detail?.guideKey !== 'companyDetail') return;
      setIsGuideActive(false);
      // 가이드 시작 전 상태로 복원 (자동으로 펼쳐졌던 목록을 다시 닫음)
      setExpandedSiteId(prevExpanded.current);
    };

    window.addEventListener('bellcloud:guide:start', handleGuideStart);
    window.addEventListener('bellcloud:guide:end', handleGuideEnd);
    return () => {
      window.removeEventListener('bellcloud:guide:start', handleGuideStart);
      window.removeEventListener('bellcloud:guide:end', handleGuideEnd);
    };
  }, [sites]);

  const handleReassignDevice = (device: Device) => {
    // 가이드용 샘플 기기는 실제 재할당 모달을 열지 않는다.
    if (device.id < 0) return;
    setReassigningDevice(device);
    setShowReassignModal(true);
  };

  const handleCloseReassignModal = () => {
    setShowReassignModal(false);
    setReassigningDevice(null);
  };

  const handleSuccessReassign = () => {
    setShowReassignModal(false);
    setReassigningDevice(null);
  };

  // 가이드 진행 중, 펼친 사이트에 기기가 없을 때 재할당 step 의 타겟을 보장하기 위한 샘플 기기.
  const guideSampleDevice = {
    id: -1,
    serial_number: t(i18nKeys.guide.sample_device),
    device_type: '-',
    status: 'active',
  } as unknown as Device;

  // 펼친 사이트에 표시할 기기 목록 (기기가 없고 가이드 중이면 샘플 한 건을 노출)
  const expandedDevicesToRender: Device[] =
    siteDevices.length > 0 ? siteDevices : isGuideActive ? [guideSampleDevice] : [];

  // 가이드 진행 중, 사이트 관리자가 없을 때 edit/delete step 의 타겟을 보장하기 위한 샘플 관리자.
  const guideSampleSiteManager = {
    id: -1,
    email: 'sample@example.com',
    name: t(i18nKeys.guide.sample_site_manager),
    phone: null,
    role_id: 0,
    company_id: company?.id ?? 0,
    site_id: null,
    is_used: false,
    sites: [],
    created_at: '',
  } as unknown as SiteManager;

  // 표시할 사이트 관리자 목록 (관리자가 없고 가이드 중이면 샘플 한 건을 노출)
  const siteManagersToRender: SiteManager[] =
    siteManagers.length > 0 ? siteManagers : isGuideActive ? [guideSampleSiteManager] : [];

  const subscriptionExpiryDisplay = useMemo((): {
    value: string;
    state: 'success' | 'warning' | 'error' | null;
  } => {
    if (!company) {
      return { value: '—', state: null };
    }
    const { text, dayNum } = formatSubscriptionExpiryLine(company.subscription_end);
    if (dayNum === null) {
      return { value: text, state: null };
    }
    let state: 'success' | 'warning' | 'error' = 'success';
    if (dayNum < 0) state = 'error';
    else if (dayNum <= 7) state = 'warning';
    return { value: text, state };
  }, [company]);

  // ==================== 렌더링 ====================
  if (loading) {
    return (
      <DefaultContents>
        {/* <MenuPath /> */}
        <Flex align="center" justify="center">
          <Spinner size="md" />
        </Flex>
      </DefaultContents>
    );
  }

  return (
    <>
      <DefaultContents>
        {/* 통계 카드 */}
        {company && (
          <div data-tour="company-summary">
            <SummaryCard
              items={[
                {
                  label: t(i18nKeys.company.total_sites),
                  value: sites?.length || 0,
                  unit: null,
                  decimal: null,
                  state: null,
                },
                {
                  label: t(i18nKeys.company.total_devices),
                  value: (() => {
                    const total =
                      sites?.reduce((sum, site) => sum + (site.device_count || 0), 0) || 0;
                    const max = company.max_devices_override;
                    return max != null ? `${total} / ${max}` : `${total} / ∞`;
                  })(),
                  unit: null,
                  decimal: null,
                  state: null,
                },
                {
                  label: t(i18nKeys.company.subscription_end_date),
                  value: subscriptionExpiryDisplay.value,
                  unit: null,
                  decimal: null,
                  state: subscriptionExpiryDisplay.state,
                },
                ...(canManageSiteManagers
                  ? [
                      {
                        label: t(i18nKeys.company.site_manager),
                        value: siteManagers?.length || 0,
                        unit: null,
                        decimal: null,
                        state: null,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        )}
        {/* 회사 정보 섹션 */}
        {company && (
          <Grid columns={isCompanyManager || isAdmin ? 2 : 1} gap={4} responsive>
            {/* LEFT: Company 정보 */}
            <Card height="auto" data-tour="company-info">
              <Flex justify="between" align="center">
                <CardTitle>{t(i18nKeys.company.company_info)}</CardTitle>
                {(isCompanyManager || isAdmin) && (
                  <IconButton
                    data-tour="company-info-edit"
                    onClick={handleEditCompany}
                    aria-label={t(i18nKeys.company.edit)}
                  >
                    <img src="/icons/edit.svg" alt="" width="30" height="30" />
                  </IconButton>
                )}
              </Flex>

              <Grid columns={2} gap={4} responsive>
                <InfoRow label={t(i18nKeys.company.company_name)} value={company.name} />
                <InfoRow
                  label={t(i18nKeys.company.company_location)}
                  value={company.location || ''}
                />
                <InfoRow
                  label={t(i18nKeys.company.company_description)}
                  value={company.description || ''}
                />
                <InfoRow
                  label={t(i18nKeys.company.company_telephone)}
                  value={formatPhoneForDisplay(company.telephone || '')}
                />
                <InfoRow
                  label={t(i18nKeys.company.subscription_end_date)}
                  value={subscriptionExpiryDisplay.value}
                />
              </Grid>
            </Card>
            {/* Company Manager 정보 */}
            <Card height="auto" data-tour="company-manager-info">
              <Flex justify="between" align="center">
                <CardTitle>{t(i18nKeys.auth.company_manager)}</CardTitle>
                {(isCompanyManager || isAdmin) && companyManagerInfo && (
                  <IconButton
                    data-tour="company-manager-edit"
                    aria-label={t(i18nKeys.company.edit)}
                    onClick={() => setShowCompanyManagerModal(true)}
                  >
                    <img src="/icons/edit.svg" alt="" width="30" height="30" />
                  </IconButton>
                )}
              </Flex>

              <Grid columns={2} gap={4} responsive>
                <InfoRow label={t(i18nKeys.auth.name)} value={companyManagerInfo?.name || ''} />
                <InfoRow
                  label={t(i18nKeys.auth.phone)}
                  value={formatPhoneForDisplay(companyManagerInfo?.phone)}
                />
                <InfoRow label={t(i18nKeys.auth.email)} value={companyManagerInfo?.email || ''} />
              </Grid>
            </Card>
          </Grid>
        )}

        {/* Site / Site Manager 영역 */}
        {company && (
          <div
            css={css`
              display: grid;
              grid-template-columns: ${canManageSiteManagers ? '1fr 1fr' : '1fr'};
              gap: ${theme.spacing[4]}px;
              flex: 1;
              min-height: 0;
              @media (max-width: ${breakpoints.lg - 1}px) {
                grid-template-columns: 1fr;
              }
            `}
          >
            {/* 사이트 관리 섹션 */}
            <Card height="auto" layout="flex" gap={4} scrollX data-tour="site-list">
              {company && (
                <>
                  <Flex justify="between" align="center">
                    <CardTitle>
                      {t(i18nKeys.company.site_management)}{' '}
                      {isCompanyManager || isAdmin ? '' : `(${t(i18nKeys.company.site_list)})`}
                    </CardTitle>
                    {(isCompanyManager || isAdmin) && (
                      <Button
                        variant="primary"
                        onClick={handleCreateSite}
                        data-tour="create-site-button"
                      >
                        {t(i18nKeys.company.site_create)}
                      </Button>
                    )}
                  </Flex>

                  {isLoadingSites ? (
                    <Flex align="center" justify="center" style={{ padding: theme.spacing[7] }}>
                      <Flex align="center" gap={4}>
                        <Spinner size="md" />
                        <Text color={theme.colors.text.secondary}>
                          {t(i18nKeys.company.loading)}
                        </Text>
                      </Flex>
                    </Flex>
                  ) : (
                    <table
                      style={styles.table}
                      role="table"
                      aria-label={t(i18nKeys.company.site_list)}
                    >
                      <thead style={styles.tableHeader}>
                        <tr>
                          <th style={styles.tableHeaderCell}>{t(i18nKeys.company.site_name)}</th>
                          <th style={styles.tableHeaderCell}>
                            {t(i18nKeys.company.site_location)}
                          </th>
                          <th style={styles.tableHeaderCell}>
                            {t(i18nKeys.company.site_manager_assigned)}
                          </th>
                          <th style={styles.tableHeaderCell}>
                            {t(i18nKeys.company.site_device_count)}
                          </th>
                          <th style={styles.tableHeaderCell}>{t(i18nKeys.company.site_action)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sites.length === 0 ? (
                          <tr>
                            <td
                              style={{
                                textAlign: 'center',
                                padding: `${theme.spacing[6]} !important`,
                                color: theme.colors.text.secondary,
                              }}
                            >
                              {t(i18nKeys.company.site_no_data)}
                            </td>
                          </tr>
                        ) : (
                          sites.map(site => (
                            <React.Fragment key={site.id}>
                              <tr
                                style={{
                                  borderBottom: `1px solid ${theme.colors.border.container}`,
                                  cursor: 'pointer',
                                  textAlign: 'center',
                                }}
                                onMouseEnter={e => {
                                  if (expandedSiteId !== site.id) {
                                    e.currentTarget.style.backgroundColor =
                                      theme.colors.surface.layout.overlay;
                                  }
                                }}
                                onClick={() => handleToggleSiteDevices(site.id)}
                                onMouseLeave={e => {
                                  if (expandedSiteId !== site.id) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <td>
                                  <Text align="center" weight="semibold">
                                    {site.name}
                                  </Text>
                                </td>
                                <td>
                                  <Text align="center" size="md">
                                    {site.location || '-'}
                                  </Text>
                                </td>
                                <td>
                                  <Text align="center" size="md">
                                    {site.site_manager_name || '-'}
                                  </Text>
                                </td>
                                <td>
                                  <Tag
                                    variant="primary"
                                    size="xs"
                                    style={{
                                      cursor: site.device_count > 0 ? 'pointer' : 'default',
                                    }}
                                    onClick={() =>
                                      site.device_count > 0 && handleToggleSiteDevices(site.id)
                                    }
                                  >
                                    {site.device_count} {t(i18nKeys.device.device)}
                                  </Tag>
                                </td>
                                <td>
                                  <Flex direction="column" align="center">
                                    <Flex gap={2} align="end" justify="center">
                                      <IconButton
                                        data-tour="site-edit"
                                        onClick={() => handleEditSite(site)}
                                        aria-label={t(i18nKeys.company.edit)}
                                      >
                                        <img src="/icons/edit.svg" alt="" width="30" height="30" />
                                      </IconButton>
                                      <IconButton
                                        data-tour="site-delete"
                                        onClick={() => handleDeleteSite(site)}
                                        disabled={sites.length <= 1}
                                        aria-label={t(i18nKeys.company.site_delete)}
                                      >
                                        <img
                                          src="/icons/delete.svg"
                                          alt=""
                                          width="30"
                                          height="30"
                                        />
                                      </IconButton>
                                    </Flex>
                                    <TextButton
                                      data-tour="site-show-devices"
                                      onClick={() => handleToggleSiteDevices(site.id)}
                                    >
                                      {expandedSiteId === site.id ? 'Hide Devices' : 'Show Devices'}
                                    </TextButton>
                                  </Flex>
                                </td>
                              </tr>
                              {/* Devices list row (expandable) */}
                              {expandedSiteId === site.id && (
                                <tr>
                                  <td colSpan={5} style={styles.expandedRow}>
                                    <Text size="lg" weight="semibold">
                                      {t(i18nKeys.datalog.registeredDevices)}
                                    </Text>
                                    {loadingSiteDevices ? (
                                      <Flex align="center" justify="center" gap={2}>
                                        <Spinner size="sm" />
                                      </Flex>
                                    ) : expandedDevicesToRender.length === 0 ? (
                                      <Text
                                        align="center"
                                        size="md"
                                        color={theme.colors.text.secondary}
                                      >
                                        {t(i18nKeys.company.device_no_data)}
                                      </Text>
                                    ) : (
                                      <table style={styles.deviceTable}>
                                        <thead style={styles.deviceTableHeader}>
                                          <tr>
                                            <th style={styles.tableCell}>
                                              {t(i18nKeys.company.device_serial_number)}
                                            </th>
                                            <th style={styles.tableCell}>
                                              {t(i18nKeys.company.device_type)}
                                            </th>
                                            <th style={styles.tableCell}>
                                              {t(i18nKeys.company.device_status)}
                                            </th>
                                            <th style={styles.tableCell}>
                                              {t(i18nKeys.company.site_action)}
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {expandedDevicesToRender.map(device => (
                                            <tr key={device.id} style={styles.tableRow}>
                                              <td style={styles.tableCell}>
                                                <Text align="center" size="sm">
                                                  {device.serial_number}
                                                </Text>
                                              </td>
                                              <td style={styles.tableCell}>
                                                <Text align="center" size="sm">
                                                  {device.device_type || '-'}
                                                </Text>
                                              </td>
                                              <td style={styles.tableCell}>
                                                <Tag
                                                  variant={
                                                    device.status === 'active'
                                                      ? 'success'
                                                      : 'warning'
                                                  }
                                                  size="xs"
                                                >
                                                  {device.status || 'unknown'}
                                                </Tag>
                                              </td>
                                              <td style={styles.tableCell}>
                                                <Button
                                                  width="50%"
                                                  variant="tertiary"
                                                  data-tour="device-reassign"
                                                  onClick={() => handleReassignDevice(device)}
                                                >
                                                  {t(i18nKeys.company.device_reassign)}
                                                </Button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </Card>

            {canManageSiteManagers && (
              <Card padding={6} height="auto" gap={4} scrollX data-tour="site-manager-card">
                {company && (
                  <>
                    <Flex justify="between" align="center">
                      <CardTitle>{t(i18nKeys.company.site_manager)}</CardTitle>
                      <Button
                        variant="primary"
                        onClick={handleCreateSiteManager}
                        data-tour="site-manager-add"
                      >
                        {t(i18nKeys.company.site_manager_add)}
                      </Button>
                    </Flex>

                    {loadingSiteManagers ? (
                      <Flex align="center" justify="center" style={{ padding: theme.spacing[7] }}>
                        <Flex align="center">
                          <Spinner size="md" />
                          <Text color={theme.colors.text.secondary}>
                            {t(i18nKeys.company.loading)}
                          </Text>
                        </Flex>
                      </Flex>
                    ) : (
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: theme.fontSize.sm,
                        }}
                        role="table"
                        aria-label={t(i18nKeys.company.site_manager_management)}
                      >
                        <thead style={{ backgroundColor: theme.colors.surface.header.default }}>
                          <tr>
                            <th
                              style={{
                                padding: theme.spacing[6],
                                textAlign: 'center',
                                fontWeight: theme.fontWeight.semibold,
                                color: theme.colors.text.primary,
                                borderBottom: `2px solid ${theme.colors.border.container}`,
                              }}
                            >
                              {t(i18nKeys.company.site_manager_name)}
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[6],
                                textAlign: 'center',
                                fontWeight: theme.fontWeight.semibold,
                                color: theme.colors.text.primary,
                                borderBottom: `2px solid ${theme.colors.border.container}`,
                              }}
                            >
                              {t(i18nKeys.company.site_manager_phone)}
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[6],
                                textAlign: 'center',
                                fontWeight: theme.fontWeight.semibold,
                                color: theme.colors.text.primary,
                                borderBottom: `2px solid ${theme.colors.border.container}`,
                              }}
                            >
                              {t(i18nKeys.company.site_manager_email)}
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[6],
                                textAlign: 'center',
                                fontWeight: theme.fontWeight.semibold,
                                color: theme.colors.text.primary,
                                borderBottom: `2px solid ${theme.colors.border.container}`,
                              }}
                            >
                              {t(i18nKeys.company.site_manager_sites)}
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[6],
                                textAlign: 'center',
                                fontWeight: theme.fontWeight.semibold,
                                color: theme.colors.text.primary,
                                borderBottom: `2px solid ${theme.colors.border.container}`,
                              }}
                            >
                              {t(i18nKeys.company.site_manager_status)}
                            </th>
                            <th
                              style={{
                                padding: theme.spacing[6],
                                textAlign: 'center',
                                fontWeight: theme.fontWeight.semibold,
                                color: theme.colors.text.primary,
                                borderBottom: `2px solid ${theme.colors.border.container}`,
                              }}
                            >
                              {t(i18nKeys.company.site_action)}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {siteManagersToRender.length === 0 ? (
                            <tr>
                              <td
                                colSpan={6}
                                style={{
                                  textAlign: 'center',
                                  padding: `${theme.spacing[6]} !important`,
                                  color: theme.colors.text.secondary,
                                }}
                              >
                                {t(i18nKeys.company.site_manager_no_data)}
                              </td>
                            </tr>
                          ) : (
                            siteManagersToRender.map(manager => (
                              <tr
                                key={manager.id}
                                style={{
                                  borderBottom: `1px solid ${theme.colors.border.container}`,
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.backgroundColor =
                                    theme.colors.surface.layout.overlay;
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <td style={{ padding: `${theme.spacing[7]} ${theme.spacing[6]}` }}>
                                  <Flex justify="center">
                                    <Text size="sm" weight="semibold">
                                      {manager.name || '-'}
                                    </Text>
                                  </Flex>
                                </td>
                                <td style={{ padding: `${theme.spacing[7]} ${theme.spacing[6]}` }}>
                                  <Flex justify="center">
                                    <Text size="sm">{formatPhoneForDisplay(manager.phone)}</Text>
                                  </Flex>
                                </td>
                                <td
                                  style={{
                                    padding: `${theme.spacing[7]} ${theme.spacing[6]}`,
                                    paddingLeft: theme.spacing[8],
                                  }}
                                >
                                  <Text weight="semibold">{manager.email}</Text>
                                </td>
                                <td>
                                  <div style={{ padding: `${theme.spacing[7]} 0` }}>
                                    {manager.sites && manager.sites.length > 0 ? (
                                      <Flex direction="column" padding={1} gap={1} align="center">
                                        {manager.sites.map(site => (
                                          <Tag key={site.id} variant="info" size="sm">
                                            {site.name}
                                          </Tag>
                                        ))}
                                      </Flex>
                                    ) : (
                                      <Flex justify="center">
                                        <Text size="sm" color={theme.colors.text.secondary}>
                                          -
                                        </Text>
                                      </Flex>
                                    )}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    padding: `${theme.spacing[4]} ${theme.spacing[6]}`,
                                    textAlign: 'center',
                                  }}
                                >
                                  <Flex justify="center">
                                    <Tag
                                      variant={manager.is_used ? 'success' : 'warning'}
                                      size="sm"
                                    >
                                      {manager.is_used
                                        ? t(i18nKeys.company.site_manager_status_active)
                                        : t(i18nKeys.company.site_manager_status_pending)}
                                    </Tag>
                                  </Flex>
                                </td>
                                <td
                                  style={{
                                    padding: `${theme.spacing[4]} ${theme.spacing[6]}`,
                                    textAlign: 'center',
                                  }}
                                >
                                  <Flex gap={2} align="center" justify="center">
                                    <IconButton
                                      data-tour="site-manager-edit"
                                      onClick={() => handleEditSiteManager(manager)}
                                      aria-label={t(i18nKeys.company.edit)}
                                    >
                                      <img src="/icons/edit.svg" alt="" width="30" height="30" />
                                    </IconButton>
                                    <IconButton
                                      data-tour="site-manager-delete"
                                      onClick={() => handleDeleteSiteManager(manager)}
                                      aria-label={t(i18nKeys.company.site_manager_delete)}
                                    >
                                      <img src="/icons/delete.svg" alt="" width="30" height="30" />
                                    </IconButton>
                                  </Flex>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </Card>
            )}
          </div>
        )}

        {/* Company Info Edit Modal — Sites 페이지와 동일하게 열릴 때만 마운트 */}
        {showCompanyInfoModal && company && (isCompanyManager || isAdmin) && (
          <CompanyInfoEditModal
            isOpen={showCompanyInfoModal}
            company={company}
            onClose={() => setShowCompanyInfoModal(false)}
            onSave={() => setShowCompanyInfoModal(false)}
          />
        )}

        {/* Company Manager Edit Modal */}
        {showCompanyManagerModal && companyManagerInfo && (isCompanyManager || isAdmin) && (
          <CompanyManagerEditModal
            isOpen={showCompanyManagerModal}
            companyManager={companyManagerInfo}
            onClose={() => setShowCompanyManagerModal(false)}
            onSave={() => setShowCompanyManagerModal(false)}
          />
        )}

        {/* Site CRUD — Sites 탭과 동일하게 열릴 때만 마운트(기존 값으로 폼 초기화) */}
        {showSiteModal && company && (isCompanyManager || isAdmin) && (
          <SiteCrudModal
            isOpen={showSiteModal}
            site={editingSite}
            companyId={company.id}
            onClose={handleCloseSiteModal}
            onSave={() => {
              handleCloseSiteModal();
            }}
          />
        )}

        {(isCompanyManager || isAdmin) && (
          <RegistrationFormModal
            isOpen={showAddSiteManagerModal}
            mode="siteManager"
            initialData={{}}
            onClose={() => {
              setShowAddSiteManagerModal(false);
            }}
            onSubmit={handleAddSiteManager}
            isLoading={createSiteManagerMutation.isPending}
            title={t(i18nKeys.company.site_manager_add_new)}
          />
        )}

        {/* Site Manager 수정 — Sites 탭과 동일하게 편집 대상이 있을 때만 마운트 */}
        {showSiteManagerModal && editingSiteManager && company && (isCompanyManager || isAdmin) && (
          <SiteManagerCrudModal
            isOpen={showSiteManagerModal}
            siteManager={editingSiteManager}
            companyId={editingSiteManager.company_id || company.id}
            onClose={handleCloseSiteManagerModal}
            onSave={async () => {
              await refetchSiteManagers();
              handleCloseSiteManagerModal();
            }}
          />
        )}

        {/* Device Reassign Modal */}
        <DeviceReassignModal
          isOpen={showReassignModal}
          device={reassigningDevice}
          sites={sites}
          onClose={handleCloseReassignModal}
          onSuccess={handleSuccessReassign}
        />
      </DefaultContents>
    </>
  );
};

export default CompanyPage;
